import { useMemo, useState } from 'react';
import { Armchair, Plus } from 'lucide-react';
import { AgendaView } from '../components/agenda/AgendaView';
import type { AgendaClient, AgendaResource, Appointment } from '../components/agenda/types';
import { api, patch, post } from './api';
import { useBusiness } from './business';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Select, Skeleton, useToast } from './ui';
import { attempt, useLoad } from './useLoad';

interface Op { id: string; name: string; active: boolean; performs_services: boolean; color: string | null; photo: string | null; schedule: { day: number; from: string; to: string }[] | null; services: { product_id: string; price: string | null; duration_min: number | null }[] }
interface Prod { id: string; name: string; short_name: string | null; kind: string; active: boolean; unit_price: string; duration_min: number | null }
interface Store { id: string; name: string }

/** Agenda na gestão: a mesma da caixa (o dono marca, muda e vê o dia de todos). */
export function AgendaPage() {
  const { has } = useBusiness();
  const ops = useLoad<{ items: Op[] }>('/operators');
  const prods = useLoad<{ items: Prod[] }>('/products');
  const res = useLoad<{ items: (AgendaResource & { active: boolean })[] }>(has('resources') ? '/resources' : '/auth/me');
  const client: AgendaClient = useMemo(() => ({
    list: async (from, to) => (await api<{ items: Appointment[] }>(`/appointments?from=${from}&to=${to}`)).items,
    create: (a) => post('/appointments', a),
    update: async (id, p) => (await patch<{ appointment: Appointment }>(`/appointments/${id}`, p)).appointment,
    // Dono e gerente: marcar por cima de outra é decisão deles (pergunta-se aqui).
    approve: async (why) => confirm(`${why}. Marcar na mesma?`),
  }), []);
  if (ops.error || prods.error) return <Alert>{ops.error ?? prods.error}</Alert>;
  if (!ops.data || !prods.data) return <Card><Skeleton rows={8} /></Card>;
  const staff = ops.data.items.filter((o) => o.active && o.performs_services).map((o) => ({
    id: o.id, name: o.name, color: o.color, photo: o.photo, schedule: o.schedule, services: o.services.map((s) => ({ productId: s.product_id, price: s.price, durationMin: s.duration_min })),
  }));
  const services = prods.data.items.filter((x) => x.kind === 'service' && x.active).map((x) => ({ id: x.id, name: x.name, shortName: x.short_name, unitPrice: x.unit_price, durationMin: x.duration_min }));
  const resources = has('resources') && Array.isArray((res.data as { items?: unknown })?.items) ? (res.data as { items: (AgendaResource & { active: boolean })[] }).items.filter((r) => r.active) : [];
  return (
    <div className="surface -mx-4 -mb-4 h-[calc(100vh-170px)] min-h-[560px] overflow-hidden rounded-2xl md:mx-0 md:mb-0">
      <AgendaView client={client} staff={staff} services={services} resources={resources} businessName={sessionStorage.getItem('makila-pos-company') ?? ''} />
    </div>
  );
}

const KIND = { room: 'Sala', chair: 'Cadeira', bed: 'Marquesa', other: 'Outro' } as const;

/** Salas, cadeiras e marquesas: uma marcação ocupa também um recurso. */
export function ResourcesPage() {
  const { data, error, reload } = useLoad<{ items: (AgendaResource & { active: boolean; store_id: string; sort_order: number })[] }>('/resources');
  const stores = useLoad<{ items: Store[] }>('/stores');
  const [f, setF] = useState({ name: '', kind: 'room', storeId: '' });
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  if (error) return <Alert>{error}</Alert>;
  if (!data || !stores.data) return <Card><Skeleton rows={4} /></Card>;
  const storeId = f.storeId || stores.data.items[0]?.id || '';
  return (
    <div className="max-w-3xl space-y-6">
      <Card title="Nova sala ou cadeira">
        <form className="grid gap-4 sm:grid-cols-[1fr_160px_auto] sm:items-end" onSubmit={async (e) => {
          e.preventDefault();
          const x = await attempt(() => post('/resources', { storeId, name: f.name, kind: f.kind }));
          setErr(x);
          if (!x) { toast('Criado'); setF({ ...f, name: '' }); void reload(); }
        }}>
          <Field label="Nome"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Sala de massagem 1" /></Field>
          <Field label="Tipo"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
          <Button icon={Plus}>Criar</Button>
        </form>
        {stores.data.items.length > 1 && (
          <div className="mt-4"><Field label="Loja"><Select value={storeId} onChange={(e) => setF({ ...f, storeId: e.target.value })}>{stores.data.items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field></div>
        )}
        {err && <div className="mt-4"><Alert>{err}</Alert></div>}
      </Card>
      <Card title="Salas e cadeiras" padded={false}>
        {!data.items.length ? <EmptyState icon={Armchair} title="Ainda não há salas nem cadeiras" text="Crie-as para a agenda não marcar duas pessoas no mesmo lugar." /> : (
          <ul className="divide-y divide-[var(--border)] p-2 xl:p-3">
            {data.items.map((r) => (
              <li key={r.id} className={`flex items-center gap-3 px-2 py-2.5 ${r.active ? '' : 'opacity-50'}`}>
                <span className="flex-1 font-medium">{r.name} <Badge>{KIND[r.kind as keyof typeof KIND] ?? r.kind}</Badge></span>
                <Button variant="ghost" size="sm" onClick={async () => { setErr(await attempt(() => patch(`/resources/${r.id}`, { active: !r.active }))); void reload(); }}>{r.active ? 'Desactivar' : 'Activar'}</Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
